/**
 * slateNames — resolve the `{slate_names}` merge field in script copy.
 *
 * This is the client mirror of `backend/app/services/slate.py:format_slate_names`.
 * Two implementations of one rule is a drift risk, and it is accepted here for
 * a specific reason: the backend resolves the merge for the *briefing* (which
 * is route-scoped and knows its slate), while `ScriptsView` is a county-wide
 * reference page with no route context and therefore no server-side slate to
 * resolve against. Keep the two in lockstep — the Oxford comma is not
 * decoration, it is what a person says out loud.
 *
 * Why the merge field at all (plan §7 / §8.2)
 * ------------------------------------------
 * The scripts blob shipped `"Hi, I'm with [Campaign]"` — an unfilled
 * placeholder sitting exactly where the candidate belongs. The plan is blunt
 * that this one string is the whole problem, and that the script is the only
 * tier of the five that actually works: it is the sentence the volunteer says
 * at the door. "A volunteer cannot read that aloud and not know who they're
 * canvassing for."
 *
 * `{slate_names}` follows the `{first_name}` convention `VoterCampaign.template`
 * already establishes (`backend/app/models/voter_campaign.py:114`) — braces,
 * snake_case, resolved at render.
 */
import type { CandidateBrief, CandidateListItem } from '@/types/candidate';

/** The merge token as it appears in script copy. */
export const SLATE_NAMES_TOKEN = '{slate_names}';

type NameableCandidate = Pick<
  CandidateBrief | CandidateListItem,
  'display_name' | 'full_name'
>;

/** Short form a volunteer says out loud; falls back to the ballot name. */
export function slateShortName(c: NameableCandidate): string {
  return (c.display_name || '').trim() || (c.full_name || '').trim();
}

/**
 * Join names the way a person says them: "Smith, Jones, and Garcia".
 *
 * Mirrors `format_slate_names` in the backend service, Oxford comma included.
 * Returns `''` for an empty slate — callers MUST surface that as missing data
 * rather than substituting something generic like "our campaign", which would
 * just re-create `[Campaign]` under a new name.
 */
export function formatSlateNames(names: string[]): string {
  const parts = names.map((n) => (n || '').trim()).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** `{slate_names}` value for a slate. `''` when empty. */
export function slateNamesFor(candidates: NameableCandidate[]): string {
  return formatSlateNames(candidates.map(slateShortName));
}

/**
 * Substitute `{slate_names}` in a line of script copy.
 *
 * ⚠️ **An empty `slateNames` leaves the token UNRESOLVED on purpose.** The
 * temptation is to swap in a neutral phrase so the sentence still reads — that
 * is precisely how `[Campaign]` survived this long. An unresolved token is a
 * visible defect that gets fixed; a plausible-sounding fallback is a defect
 * that ships forever. Callers should detect the leftover token (see
 * `hasUnresolvedSlate`) and flag it loudly.
 */
export function mergeSlateNames(copy: string, slateNames: string): string {
  if (!copy) return copy;
  if (!slateNames) return copy;
  return copy.split(SLATE_NAMES_TOKEN).join(slateNames);
}

/** Did a line come through with its merge field still unresolved? */
export function hasUnresolvedSlate(copy: string): boolean {
  return !!copy && copy.includes(SLATE_NAMES_TOKEN);
}
